import { FeatureOrigin, Style } from "@hydrosoft/map-api";
import { ORDER_METHODS, } from "./constants";
import { Point as SalesmanPoint, solve as salesmanSolve } from "./salesman";
import GeoJSONReader from "jsts/org/locationtech/jts/io/GeoJSONReader";
import BufferOp from "jsts/org/locationtech/jts/operation/buffer/BufferOp";
import RelateOp from "jsts/org/locationtech/jts/operation/relate/RelateOp";
import OlPoint from "ol/geom/Point";
import { Extent, Feature, Geometry, } from "@hydrosoft/map-api";
export class PointGridSettings {
    _spacing;
    _rotation;
    _buffer;
    _offsetX;
    _offsetY;
    _onChange = [];
    constructor(spacing, rotation, buffer, offsetX, offsetY) {
        this._spacing = spacing;
        this._rotation = rotation;
        this._buffer = buffer;
        this._offsetX = offsetX;
        this._offsetY = offsetY;
    }
    getSpacing = (formatted = false) => {
        if (formatted) {
            return this._withUnits(this._spacing.value, this._spacing.units);
        }
        return this._spacing.value;
    };
    setSpacing = (spacing) => {
        this._spacing.value = spacing;
        this._callOnChange("spacing", spacing);
    };
    _withUnits(value, units) {
        if (units !== "m" && units !== "°") {
            if (units === "ha") {
                value = Math.round(value / 10_000);
            }
            else if (units === "km") {
                value = Math.round(value / 1_000);
            }
            else if (units.startsWith("km")) {
                value = Math.round(value / 1_000_000);
            }
        }
        return `${value} ${units}`;
    }
    getRotation = (formatted = false) => {
        if (formatted) {
            return this._withUnits(this._rotation.value, this._rotation.units);
        }
        return this._rotation.value;
    };
    setRotation = (rotation) => {
        this._rotation.value = rotation;
        this._callOnChange("rotation", rotation);
    };
    getBuffer = (formatted = false) => {
        if (formatted) {
            return this._withUnits(this._buffer.value, this._buffer.units);
        }
        return this._buffer.value;
    };
    setBuffer = (buffer) => {
        this._buffer.value = buffer;
        this._callOnChange("buffer", buffer);
    };
    getOffsetX = (formatted = false) => {
        if (formatted) {
            return this._withUnits(this._offsetX.value, this._offsetX.units);
        }
        return this._offsetX.value;
    };
    setOffsetX = (offsetX) => {
        this._offsetX.value = offsetX;
        this._callOnChange("offsetX", offsetX);
    };
    getOffsetY = (formatted = false) => {
        if (formatted) {
            return this._withUnits(this._offsetY.value, this._offsetY.units);
        }
        return this._offsetY.value;
    };
    setOffsetY = (offsetY) => {
        this._offsetY.value = offsetY;
        this._callOnChange("offsetY", offsetY);
    };
    _callOnChange = (type, value) => {
        this._onChange.forEach((callback) => {
            callback.call(this, {
                type: type,
                value: value,
            });
        });
    };
    onChange = (callback) => {
        this._onChange.push(callback);
    };
}
export class PointGridClass {
    _settings;
    _mask;
    _maskFeature;
    _map;
    _graphicsLayerId;
    _style;
    _label;
    _geojsonReader;
    constructor(map, mask, graphicsLayerId, settings, style, label) {
        this._map = map;
        this._graphicsLayerId = graphicsLayerId;
        this._style = style;
        this._label = label;
        this._settings = settings;
        this._settings.onChange((change) => {
            if (change.type === "buffer") {
                this._maskFeature = this._getMaskFeature(this._mask, false);
            }
            this.render();
        });
        this._geojsonReader = new GeoJSONReader();
        this.setMask(mask);
    }
    setMask = (mask) => {
        this._mask = mask;
        this._maskFeature = this._getMaskFeature(mask, true);
    };
    getSettings = () => {
        return this._settings;
    };
    getPointCount = () => {
        return this._map
            .getGraphics(this._graphicsLayerId)
            .getSource()
            .getFeatures().length;
    };
    render = () => {
        this.clear();
        const gridGeometries = this._getGridGeometries(this._maskFeature.bbox);
        const transform = this._getTransform(gridGeometries);
        const gridMatrix = this._getGridMatrix(gridGeometries.bbox, transform);
        this._renderGrid(gridMatrix, this._maskFeature);
    };
    clear = () => {
        this._map.clearGraphics(this._graphicsLayerId);
    };
    order = (startNumber, method) => {
        const features = this._getFeatures();
        if (features.length < 1) {
            return;
        }
        switch (method) {
            case ORDER_METHODS.SALESMAN:
                this._orderSalesman(startNumber, features);
                break;
            case ORDER_METHODS.SNAKE:
                this._orderSnake(startNumber, features);
                break;
            default:
                break;
        }
    };
    export = () => {
        return this._getFeatures().map((feature) => {
            return {
                type: "Feature",
                id: feature.getId(),
                geometry: JSON.parse(feature
                    .getGeometry()
                    .toGeoJSON(this._map
                    .getMap()
                    .getView()
                    .getProjection()
                    .getMetersPerUnit() === 1
                    ? 2
                    : 5)),
                properties: [],
            };
        });
    };
    /** PRIVATE METHODS */
    _orderSalesman = (startNumber, features) => {
        if (features.length === 0) {
            return;
        }
        const points = [];
        features.map((feature) => {
            const coords = (feature.getGeometry().toPoint().getCoordinates());
            points.push(new SalesmanPoint(coords[0], coords[1]));
        });
        //const textStyle = this._textStyle ? this._textStyle : TEXT_STYLE;
        const textStyle = this._label;
        const solution = salesmanSolve(points, undefined, undefined);
        const style = { ...this._style, label: textStyle };
        this.clear();
        for (let i = 0; i < features.length; i++) {
            const feature = features[i];
            const number = startNumber + solution.indexOf(i);
            const coords = (feature.getGeometry().toPoint().getCoordinates());
            this._renderPoint(feature.getId(), number, coords, style);
        }
    };
    _orderSnake = (startNumber, features) => {
        if (features.length === 0) {
            return;
        }
        const points = [];
        // get rotation coefficients
        const rotationRad = (this._settings.getRotation() * Math.PI) / 180;
        const sinRot = Math.sin(rotationRad);
        const cosRot = Math.cos(rotationRad);
        const gridGeometries = this._getGridGeometries(this._maskFeature.bbox);
        const center = gridGeometries.center;
        features.map((feature) => {
            const coords = (feature.getGeometry().toPoint().getCoordinates());
            points.push({
                feature,
                coords: [coords[0], coords[1]],
                coordsInvert: [
                    center[0] +
                        (coords[0] - center[0]) * cosRot -
                        (coords[1] - center[1]) * sinRot,
                    center[1] +
                        (coords[1] - center[1]) * cosRot +
                        (coords[0] - center[0]) * sinRot,
                ],
            });
        });
        const compareX = (a, b) => {
            if (a.coordsInvert[0] < b.coordsInvert[0]) {
                return -1;
            }
            if (a.coordsInvert[0] > b.coordsInvert[0]) {
                return 1;
            }
            return 0;
        };
        const compareY = (a, b) => {
            if (a.coordsInvert[1] > b.coordsInvert[1]) {
                return -1;
            }
            if (a.coordsInvert[1] < b.coordsInvert[1]) {
                return 1;
            }
            return 0;
        };
        //const sortX: TPoint[] = points.sort( compareX ); // sort from left to right
        const sortY = points.sort(compareY); // sort from top to bottom
        // add snake like order
        // get lines (rows)
        const rows = [];
        let rowCoord = undefined;
        for (let i = 0, rowIdx = -1; i < sortY.length; i++) {
            if (rowCoord === undefined ||
                Math.abs(sortY[i].coordsInvert[1] - rowCoord) > 0.1) {
                // new row
                rowCoord = sortY[i].coordsInvert[1];
                rowIdx++;
            }
            if (rows[rowIdx] === undefined) {
                rows[rowIdx] = [];
            }
            rows[rowIdx].push(sortY[i]);
        }
        // flip order of every second row to get snake like order
        const snakeOrder = [];
        for (let i = 0; i < rows.length; i++) {
            rows[i] = rows[i].sort(compareX); // order row by x coordinate (from left to right)
            if (i % 2 == 0) {
                for (let j = 0; j < rows[i].length; j++) {
                    snakeOrder.push(rows[i][j]);
                }
            }
            else {
                // flip order for every odd row
                for (let j = rows[i].length - 1; j > -1; j--) {
                    snakeOrder.push(rows[i][j]);
                }
            }
        }
        const style = { ...this._style, label: this._label };
        this._map.clearGraphics(this._graphicsLayerId);
        for (let i = 0; i < snakeOrder.length; i++) {
            const feature = snakeOrder[i].feature;
            const number = startNumber + i;
            const coords = (feature.getGeometry().toPoint().getCoordinates());
            this._renderPoint(feature.getId(), number, coords, style);
        }
    };
    _getMaskFeature = (mask, useInnerRings) => {
        //const feature: IFeature = mask.features[0];
        const geometry = this._geojsonReader.read(mask);
        if (!useInnerRings) {
            geometry.coordinates = [
                geometry.getExteriorRing().getCoordinates(),
            ];
        }
        const bufferValue = -1 * this._settings.getBuffer();
        return {
            geometry: geometry,
            buffer: BufferOp.bufferOp(geometry, bufferValue),
            bbox: new Geometry(JSON.stringify(mask), this._map.getCrs()).getBbox(),
        };
    };
    _renderGrid = (gridMatrix, feature) => {
        let pointId = 0;
        for (let i = 0; i < gridMatrix.length; i++) {
            for (let j = 0; j < gridMatrix[i].length; j++) {
                if (RelateOp.intersects(feature.buffer, this._geojsonReader.read({
                    type: "Point",
                    coordinates: gridMatrix[i][j],
                }))) {
                    this._renderPoint(pointId, pointId, gridMatrix[i][j], this._style);
                    pointId++;
                }
            }
        }
    };
    _renderPoint = (id, number, coordinates, style) => {
        const feature = new Feature(id, Geometry.fromOl(new OlPoint(coordinates), this._map.getCrs()));
        feature.setStyle(new Style(this._style)).addProperty("number", number);
        this._map.addFeature(feature, this._graphicsLayerId, FeatureOrigin.Draw, style);
    };
    _getGridGeometries = (featureBbox) => {
        // feature bbox top left corner
        const featureTopLeft = [featureBbox[0], featureBbox[3]];
        // feature bbox centroid
        const center = Extent.fromBbox(featureBbox, this._map.getCrs()).getCentroid();
        // radius or distance from centroid to top laft corner
        const r = this._getDistance(center, featureTopLeft);
        // point grid bounding box
        const bbox = [
            center[0] - r,
            center[1] - r,
            center[0] + r,
            center[1] + r,
        ];
        // point grid top left corner
        const topLeft = [bbox[0], bbox[3]];
        return { bbox, topLeft, center, r };
    };
    _getTransform = (gridGeometries) => {
        // get transform geometries
        const TL = gridGeometries.topLeft;
        const C = gridGeometries.center;
        // get rotation coefficients
        const rotationRad = (this._settings.getRotation() * Math.PI) / 180;
        const sinRot = Math.sin(rotationRad);
        const cosRot = Math.cos(rotationRad);
        // get offset
        const offsetX = this._settings.getOffsetX();
        const offsetY = this._settings.getOffsetY();
        // get transform coefficients
        const dx = TL[0] - C[0];
        const dy = TL[1] - C[1];
        const Kx = C[0] + offsetX + dx * cosRot + dy * sinRot;
        const Ky = C[1] + offsetY + dy * cosRot - dx * sinRot;
        const spacing = Math.sqrt(this._settings.getSpacing());
        const sinX = spacing * sinRot;
        const sinY = spacing * sinRot;
        const cosX = spacing * cosRot;
        const cosY = spacing * cosRot;
        return { Kx, Ky, sinX, sinY, cosX, cosY };
    };
    _getDistance = (P1, P2) => {
        const Dx = Math.abs(P1[0] - P2[0]);
        const Dy = Math.abs(P1[1] - P2[1]);
        return Math.sqrt(Dx * Dx + Dy * Dy);
    };
    _getFeatures = () => {
        const features = this._map
            .getGraphics(this._graphicsLayerId)
            .getSource()
            .getFeatures()
            .map((feature) => {
            return Feature.fromOl(feature, this._map.getCrs());
        });
        return features.length > 0 ? features : [];
    };
    _getGridMatrix = (gridBbox, transform) => {
        const gridMatrix = [];
        const spacing = Math.sqrt(this._settings.getSpacing());
        const colNum = Math.floor((gridBbox[2] - gridBbox[0]) / spacing);
        const rowNum = Math.floor((gridBbox[3] - gridBbox[1]) / spacing);
        for (let i = 0; i <= rowNum; i++) {
            gridMatrix.push([]);
            for (let j = 0; j <= colNum; j++) {
                gridMatrix[i].push([
                    transform.Kx + j * transform.cosX - i * transform.sinY,
                    transform.Ky - i * transform.cosY - j * transform.sinX,
                ]);
            }
        }
        return gridMatrix;
    };
}
//# sourceMappingURL=pointGridClass.js.map